import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import 'reactflow/dist/style.css';

import {
  Box, Button, TextField, Radio, FormControlLabel, Checkbox, Divider, Tooltip,
  Typography, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Paper, Chip, Stack
} from '@mui/material';

import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MessageIcon from '@mui/icons-material/Message';
import LoopIcon from '@mui/icons-material/Loop';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InputIcon from '@mui/icons-material/Input';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ReplayIcon from '@mui/icons-material/Replay';
import MapIcon from '@mui/icons-material/Map';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { FaBell } from 'react-icons/fa';

import ReactFlow, {
  addEdge, applyNodeChanges, applyEdgeChanges, useReactFlow,
  MiniMap, Controls, Background, useUpdateNodeInternals, SelectionMode,
} from 'reactflow';

import CustomNode from './CustomNode';
import { saveAs } from 'file-saver';
import useAutosaveFlow, { loadDraft } from './useAutosaveFlow';
import useHistory from './useHistory';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IconButton } from '@mui/material';

import HelpModal from './components/HelpModal';

const nodeTypes = { default: CustomNode };

const NODE_ICONS = {
  TEXT: <MessageIcon sx={{ color: '#2196f3' }} fontSize="small" />,
  FILE: <InsertDriveFileIcon sx={{ color: '#B22222' }} fontSize="small" />,
  GPT: <SmartToyIcon sx={{ color: '#0055CC' }} fontSize="small" />,
  ATENDIMENTO: <LoopIcon sx={{ color: '#8800CC' }} fontSize="small" />,
  CADASTRAR: <AssignmentIndIcon sx={{ color: '#14631d' }} fontSize="small" />,
  EXCLUIR: <DeleteForeverIcon sx={{ color: '#c01414' }} fontSize="small" />,
  VCARD: <ContactPhoneIcon sx={{ color: '#2b8c2b' }} fontSize="small" />,
  CAPTURA: <InputIcon sx={{ color: '#DE911D' }} fontSize="small" />,
  ROUTER: <AccountTreeIcon sx={{ color: '#004080' }} fontSize="small" />,
};
const NODE_LABELS = {
  TEXT: 'Etapa',
  FILE: 'File',
  GPT: 'GPT',
  ATENDIMENTO: 'Trocar atendimento',
  CADASTRAR: 'Cadastrar (BD)',
  EXCLUIR: 'Excluir (BD)',
  VCARD: 'vCard',
  CAPTURA: 'Capturar Campo',
  ROUTER: 'Router',
};

// Modelos oferecidos pelo Flow Editor
const SUGGESTED_MODELS = [
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-nano',

  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4.1-nano',

  'gpt-4o',
  'gpt-4o-mini',

  'gpt-4-turbo',
  'gpt-4',
  'gpt-3.5-turbo',
];

// Informações e recomendações exibidas na seleção de modelos
const MODEL_CATALOG = {
  'gpt-5': {
    group: 'Atual',
    tagline: 'Maior capacidade para tarefas complexas.',
    ideal: 'Atendimento complexo, análise e regras extensas',
    defaults: {
      temperature: 0.2,
      maxTokens: 1000,
      sendHistory: true,
      historyLimit: 100,
    },
  },

  'gpt-5-mini': {
    group: 'Atual',
    tagline: 'Equilíbrio entre capacidade, velocidade e custo.',
    ideal: 'Atendimento completo e pedidos pelo WhatsApp',
    defaults: {
      temperature: 0.2,
      maxTokens: 1000,
      sendHistory: true,
      historyLimit: 100,
    },
  },

  'gpt-5-nano': {
    group: 'Atual',
    tagline: 'Modelo econômico para tarefas simples e objetivas.',
    ideal: 'Roteamento, classificação e respostas curtas',
    defaults: {
      temperature: 0,
      maxTokens: 100,
      sendHistory: false,
      historyLimit: 10,
    },
  },

  'gpt-4.1': {
    group: 'Anterior',
    tagline: 'Bom seguimento de instruções e prompts extensos.',
    ideal: 'Fluxos existentes que dependem do GPT-4.1',
    defaults: {
      temperature: 0.2,
      maxTokens: 1000,
      sendHistory: true,
      historyLimit: 100,
    },
  },

  'gpt-4.1-mini': {
    group: 'Anterior',
    tagline: 'Versão econômica do GPT-4.1.',
    ideal: 'Atendimentos existentes com custo moderado',
    defaults: {
      temperature: 0.2,
      maxTokens: 1000,
      sendHistory: true,
      historyLimit: 100,
    },
  },

  'gpt-4.1-nano': {
    group: 'Anterior',
    tagline: 'Versão pequena para tarefas objetivas.',
    ideal: 'Classificação, extração e validação simples',
    defaults: {
      temperature: 0,
      maxTokens: 100,
      sendHistory: false,
      historyLimit: 10,
    },
  },

  'gpt-4o': {
    group: 'Anterior',
    tagline: 'Modelo multimodal de uso geral.',
    ideal: 'Compatibilidade com fluxos existentes',
    defaults: {
      temperature: 0.3,
      maxTokens: 500,
      sendHistory: true,
      historyLimit: 50,
    },
  },

  'gpt-4o-mini': {
    group: 'Anterior',
    tagline: 'Modelo rápido e econômico da família GPT-4o.',
    ideal: 'Roteamento e respostas curtas em fluxos existentes',
    defaults: {
      temperature: 0,
      maxTokens: 100,
      sendHistory: false,
      historyLimit: 10,
    },
  },

  'gpt-4-turbo': {
    group: 'Legado',
    tagline: 'Modelo antigo da linha GPT-4.',
    ideal: 'Somente compatibilidade com integrações antigas',
    defaults: {
      temperature: 0.3,
      maxTokens: 500,
      sendHistory: true,
      historyLimit: 20,
    },
  },

  'gpt-4': {
    group: 'Legado',
    tagline: 'Modelo clássico da geração GPT-4.',
    ideal: 'Somente compatibilidade',
    defaults: {
      temperature: 0.3,
      maxTokens: 500,
      sendHistory: true,
      historyLimit: 20,
    },
  },

  'gpt-3.5-turbo': {
    group: 'Legado',
    tagline: 'Modelo muito antigo para novas integrações.',
    ideal: 'Somente quando um sistema antigo exigir',
    defaults: {
      temperature: 0.3,
      maxTokens: 300,
      sendHistory: false,
      historyLimit: 10,
    },
  },
};

// Configurações rápidas exibidas no painel do nó GPT
const GPT_PRESETS = [
  {
    id: 'atendimento',
    name: 'Atendimento completo',
    short: 'Atendimento',
    desc: 'Mantém o histórico e administra pedidos completos.',
    config: {
      model: 'gpt-5-mini',
      temperature: 0.2,
      maxTokens: 1000,
      sendHistory: true,
      historyLimit: 100,
    },
  },
  {
    id: 'premium',
    name: 'Atendimento complexo',
    short: 'Complexo',
    desc: 'Maior capacidade para regras extensas e situações difíceis.',
    config: {
      model: 'gpt-5',
      temperature: 0.2,
      maxTokens: 1000,
      sendHistory: true,
      historyLimit: 100,
    },
  },
  {
    id: 'wp',
    name: 'Roteador WP',
    short: 'WP',
    desc: 'Responde somente com o número do destino.',
    config: {
      model: 'gpt-5-nano',
      temperature: 0,
      maxTokens: 20,
      sendHistory: false,
      historyLimit: 2,
    },
  },
  {
    id: 'extracao',
    name: 'Extração e validação',
    short: 'Extração',
    desc: 'Extrai ou valida uma informação isolada.',
    config: {
      model: 'gpt-5-nano',
      temperature: 0,
      maxTokens: 100,
      sendHistory: false,
      historyLimit: 10,
    },
  },
  {
    id: 'criativo',
    name: 'Criativo e marketing',
    short: 'Criativo',
    desc: 'Cria textos comerciais com maior variação.',
    config: {
      model: 'gpt-5-mini',
      temperature: 0.7,
      maxTokens: 500,
      sendHistory: true,
      historyLimit: 20,
    },
  },
];

// Configuração inicial de todo novo nó GPT
const DEFAULT_GPT_NODE = {
  model: 'gpt-5-mini',
  temperature: 0.2,
  maxTokens: 500,
  sendHistory: false,
  historyLimit: 10,
};



const GPT_PLACEHOLDER = `Exemplo de Prompt:
Você é um atendente educado.
Responda dúvidas sobre os nossos produtos.
Nunca peça dados sensíveis.`;
const CAMPOS_SISTEMA = ['SAUDACAO', 'DATA_HORA', 'DATA', 'HORA', 'RANDOM'];

const getNextGlobalId = (pages) => {
  const allIds = pages.flatMap((p) => p.nodes.map((n) => n.id));
  let max = 0;
  for (const id of allIds) {
    const num = parseInt(id, 10);
    if (!isNaN(num) && num > max) max = num;
  }
  let next = max + 1 || 1;
  while (allIds.includes(String(next))) next++;
  return String(next);
};

const listarCamposCapturados = (pages) => {
  const set = new Set();
  pages.forEach((page) =>
    page.nodes.forEach((n) =>
      n.data.type === 'CAPTURA' && n.data.capturaCampo && set.add(n.data.capturaCampo)
    )
  );
  return Array.from(set);
};

// helper: pega chave de URL ou do localStorage
function getChaveLicenca() {
  const qs = new URLSearchParams(window.location.search);
  const daUrl = qs.get('chave');
  if (daUrl) {
    localStorage.setItem('chave_licenca', daUrl);
    return daUrl;
  }
  return localStorage.getItem('chave_licenca') || '';
}

/* ===================== Validação obrigatórios (com ELO-condicional) ===================== */
// Retorna string com o motivo se o nó está inválido; senão null.
// Nota: recebe `pages` para poder validar ELO quando usado por RETURN.
function getMissingReason(node, pages) {
  const t = (node?.data?.type || '').toUpperCase();

  if (t === 'GPT') {
    const model = (node?.data?.gpt?.model || '').trim();
    if (!model) return 'GPT sem modelo';
  
    const lbl = (node?.data?.label || '').trim();
    if (!lbl) return 'GPT sem conteúdo (prompt)';
  }
  
  if (t === 'FILE') {
    const caminho = node?.data?.caminho?.trim();
    if (!caminho) return 'FILE sem caminho do arquivo';
  }

  if (t === 'CAPTURA') {
    const campo = node?.data?.capturaCampo?.trim();
    if (!campo) return 'CAPTURA sem nome do campo';
  }

  if (t === 'ELO') {
    const dest = String(node?.data?.destino || '').trim();
    // se tiver destino, ele PRECISA existir
    if (dest) {
      const existe = (pages || []).some(pg => (pg.nodes || []).some(nd => String(nd.id) === dest));
      if (!existe) return 'ELO com destino apontando para ID inexistente';
    }
    // se for "ponto de retorno" (tem .retorno preenchido), então:
    const ret = String(node?.data?.retorno || '').trim();
    if (ret) {
      // 1) precisa ter destino (para onde entra nesse subfluxo)
      if (!dest) return 'ELO marcado para RETURN sem destino definido';
      // 2) o retorno (para onde volta) precisa existir
      const retExiste = (pages || []).some(pg => (pg.nodes || []).some(nd => String(nd.id) === ret));
      if (!retExiste) return 'ELO com retorno apontando para ID inexistente';
    }
  }

  if (t === 'VCARD') {
    const nome = node?.data?.nome?.trim();
    const tel  = node?.data?.telefone?.trim();
    if (!nome || !tel) return 'VCARD sem nome e/ou telefone';
  }

  if (t === 'TEXT') {
    const lbl = node?.data?.label?.trim();
    if (!lbl) return 'Etapa TEXT sem conteúdo';
  }

  // ATENDIMENTO, STOP, RETURN não têm obrigatórios
  return null;
}

// Valida todas as páginas e retorna { ok, errors, invalidIds }
function validatePages(pages) {
  const errors = [];
  const invalidIds = new Set();

  // validação nó a nó
  for (const page of pages || []) {
    for (const n of page.nodes || []) {
      const reason = getMissingReason(n, pages);
      if (reason) {
        errors.push({ pageId: page.id, pageName: page.name, nodeId: n.id, type: n?.data?.type, reason });
        invalidIds.add(String(n.id));
      }
    }
  }

  // --- INÍCIO GLOBAL (pode estar em QUALQUER página) ---
  const anyNodes = (pages || []).some(pg => (pg.nodes?.length || 0) > 0);
  if (anyNodes) {
    const startDefs = [];
    for (const pg of pages || []) {
      const sid = String(pg.startNodeId || '').trim();
      if (!sid) continue;
      const exists = (pg.nodes || []).some(n => String(n.id) === sid);
      startDefs.push({ pageId: pg.id, pageName: pg.name, nodeId: sid, exists });
    }
    if (startDefs.length === 0) {
      errors.push({ pageId: '-', pageName: '(geral)', nodeId: '-', type: 'FLOW', reason: 'Fluxo sem INÍCIO definido' });
    } else {
      const invalidStart = startDefs.find(s => !s.exists);
      if (invalidStart) {
        errors.push({
          pageId: invalidStart.pageId,
          pageName: invalidStart.pageName,
          nodeId: invalidStart.nodeId,
          type: 'FLOW',
          reason: `INÍCIO aponta para ID inexistente (#${invalidStart.nodeId})`,
        });
      }
      // (opcional) bloquear múltiplos INÍCIOS globais:
      if (startDefs.length > 1) {
         errors.push({ pageId: '-', pageName: '(geral)', nodeId: '-', type: 'FLOW', reason: 'Mais de um INÍCIO definido (deixe apenas um)' });
      }
    }
  }

  return { ok: errors.length === 0, errors, invalidIds };
}

/* ===================== Diálogo MAPA (templates de prompt) ===================== */
function MapDialog({ open, onClose, onUse, pages = [] }) {
  // Varre TODAS as páginas e junta os Way-Points (data.wp preenchido)
  const scanned = useMemo(() => {
    const wps = [];
    (pages || []).forEach((pg) => {
      (pg.nodes || []).forEach((n) => {
        const wp = String(n?.data?.wp || '').trim();
        if (wp) {
          wps.push({
            id: String(n.id),
            wp,
            pageId: pg.id,
            pageName: pg.name || `Página ${pg.id}`,
          });
        }
      });
    });
    wps.sort((a, b) => Number(a.id) - Number(b.id));
    const fallbackWp = wps.find((w) => /^outros?$/i.test(w.wp)) || wps[0] || null;
    return { wps, fallbackWp };
  }, [pages]);

  // Prompt base (roteamento por Way-Point) — formato “número puro”
  const basePrompt = useMemo(() => {
    const wpIds = scanned.wps.map(w => String(w.id));
    const wpOpts = scanned.wps.map(w => `${w.id}=${w.wp}`).join(', ');
    const idsList = wpIds.length ? `(${wpIds.join(', ')})` : '(0)';
    const optsLine = wpIds.length ? `OPÇÕES: ${wpOpts}.` : 'OPÇÕES: 0=desconhecido.';

    return [
      '[WP]',
      'Interprete uma mensagem para saber o que o usuário precisa',
      `Responda APENAS com número, nada além de número entre ${idsList}.`,
      optsLine,
      'Se tiver dúvida, responda 0.',
      '',
      'Mensagem a ser interpretada é:'
    ].join('\n');
  }, [scanned.wps]);

  // Estado do texto (somente o base)
  const [promptText, setPromptText] = useState('');
  useEffect(() => {
    if (open) setPromptText(basePrompt);
  }, [open, basePrompt]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Gerador de Prompt (MAPA WAY-POINTs de todas as páginas)</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          {/* Lista de Way-Points (informativa, pode manter) */}
          <Box sx={{ p: 1, border: '1px solid #eee', borderRadius: 1, bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Way-Points encontrados (todas as páginas)
            </Typography>
            <div style={{ maxHeight: 200, overflow: 'auto', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.4 }}>
              {scanned.wps.length === 0 ? (
                <div>(nenhum Way-Point encontrado)</div>
              ) : (
                scanned.wps.map((w) => (
                  <div key={`${w.pageId}-${w.id}`}>
                    [{w.pageName}] #{w.id} [WP] — {w.wp}
                  </div>
                ))
              )}
            </div>
          </Box>

          {/* Prompt final (editável) — SOMENTE o base */}
          <TextField
            label="Prompt gerado"
            multiline
            minRows={10}
            fullWidth
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setPromptText(basePrompt)}>Restaurar</Button>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={() => onUse(promptText)}>
          Usar no GPT
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ModelPickerDialog({ open, onClose, onSelect, currentModel }) {
  const [query, setQuery] = React.useState('');
  const [selected, setSelected] = React.useState(currentModel || '');

  React.useEffect(() => {
    if (open) {
      setSelected(currentModel || '');
      setQuery('');
    }
  }, [open, currentModel]);

  const allModels = React.useMemo(() => {
    const set = new Set([...SUGGESTED_MODELS, ...Object.keys(MODEL_CATALOG)]);
    return Array.from(set);
  }, []);

  const filtered = allModels.filter((id) =>
    id.toLowerCase().includes(query.toLowerCase())
  );

  const groups = React.useMemo(() => {
    const order = {
      Atual: 1,
      Anterior: 2,
      Legado: 3,
      Outros: 4,
    };

    const map = new Map();

    filtered.forEach((id) => {
      const group = MODEL_CATALOG[id]?.group || 'Outros';
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(id);
    });

    return Array.from(map.entries()).sort(
      (a, b) => (order[a[0]] || 99) - (order[b[0]] || 99)
    );
  }, [filtered]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Selecionar modelo</DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar modelo..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ maxHeight: 380, overflow: 'auto', pr: 0.5 }}>
          {groups.map(([group, ids]) => (
            <Box key={group} sx={{ mb: 1.5 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', pl: 0.5 }}>
                {group}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 1,
                }}
              >
                {ids.map((id) => {
                  const info = MODEL_CATALOG[id] || {};
                  const modelGroup = info.group || 'Outros';
                  const isCurrent = modelGroup === 'Atual';
                  const isPrevious = modelGroup === 'Anterior';
                  const isLegacy = modelGroup === 'Legado';
                  const isSelected = selected === id;

                  const cardBackground = isLegacy
                    ? '#fffbe6'
                    : isPrevious
                      ? '#f5f5f5'
                      : '#f8fbff';

                  const chipLabel = isCurrent
                    ? 'Atual'
                    : isPrevious
                      ? 'Anterior'
                      : isLegacy
                        ? 'Legado'
                        : 'Outro';

                  const chipColor = isCurrent
                    ? 'success'
                    : isLegacy
                      ? 'warning'
                      : 'default';

                  return (
                    <Paper
                      key={id}
                      onClick={() => setSelected(id)}
                      onDoubleClick={() => onSelect(id, false)}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: isSelected
                          ? '2px solid #1976d2'
                          : '1px solid #e5e7eb',
                        bgcolor: cardBackground,
                        '&:hover': {
                          boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                        },
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>{id}</Typography>
                          <Chip
                            size="small"
                            label={chipLabel}
                            color={chipColor}
                            variant={isCurrent ? 'filled' : 'outlined'}
                          />
                        </Box>

                        {info.tagline && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {info.tagline}
                          </Typography>
                        )}

                        {info.ideal && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Ideal: {info.ideal}
                          </Typography>
                        )}

                        {!!info.defaults && (
                          <Chip
                            size="small"
                            label="Tem recomendação"
                            variant="outlined"
                            sx={{ width: 'fit-content', mt: 0.5 }}
                          />
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          ))}

          {filtered.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', p: 1 }}>
              Nenhum modelo encontrado
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button disabled={!selected} onClick={() => onSelect(selected, false)}>
          Usar
        </Button>
        <Tooltip
          title={
            MODEL_CATALOG[selected]?.defaults
              ? 'Aplica temperatura, limite de resposta e histórico recomendados'
              : 'Este modelo não possui recomendação cadastrada'
          }
        >
          <span>
            <Button
              variant="contained"
              disabled={!selected || !MODEL_CATALOG[selected]?.defaults}
              onClick={() => onSelect(selected, true)}
            >
              Usar + recomendação
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

/* ===================== Flow principal ===================== */

export default function FlowEditorGpt() {
  useEffect(() => {
    document.title = 'Flow Editor';

    // favicon ⚡
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">⚡</text></svg>`;
    const href = 'data:image/svg+xml,' + encodeURIComponent(svg);
    let favicon = document.querySelector('link[rel="icon"][data-flow-favicon]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.setAttribute('rel', 'icon');
      favicon.setAttribute('data-flow-favicon', '1');
      document.head.appendChild(favicon);
    }
    favicon.type = 'image/svg+xml';
    favicon.href = href;

    // 1) captura e persiste a chave (se veio na URL)
    getChaveLicenca();

    // 2) limpa a query string da barra sem perder a chave
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);
  }, []);

  // licença
  const [verificandoChave, setVerificandoChave] = useState(true);
  const [podeUsarFlow, setPodeUsarFlow] = useState(false);

  useEffect(() => {
    (async () => {
      setVerificandoChave(true);
      try {
        const chave = getChaveLicenca();
        const resp = await fetch(`/api/fluxos/verificar-licenca?chave=${encodeURIComponent(chave)}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const { podeUsarFlow } = await resp.json();
        setPodeUsarFlow(!!podeUsarFlow);
      } catch (e) {
        setPodeUsarFlow(false);
      } finally {
        setVerificandoChave(false);
      }
    })();
  }, []);

  // Modal de seleção de modelo (para o GPT) //
  const [modelDialogOpen, setModelDialogOpen] = useState(false);

  // estado principal (sem gptDefaults por página)
  const [pages, setPages] = useState([
    {
      id: 1,
      name: 'Página 1',
      nodes: [
        { id: '1', type: 'default', position: { x: 250, y: 5 }, data: { id: '1', label: '🚀 Início\nDigite 1 para suporte\nDigite 2 para vendas', type: 'TEXT' } },
        { id: '2', type: 'default', position: { x: 100, y: 200 }, data: { id: '2', label: '🛠️ Suporte\nQual seu problema?', type: 'TEXT' } },
        { id: '3', type: 'default', position: { x: 400, y: 200 }, data: { id: '3', label: '🛒 Vendas\nQual produto você quer?', type: 'TEXT' } }
      ],
      edges: [
        { id: 'e1-2', source: '1', target: '2', label: '1' },
        { id: 'e1-3', source: '1', target: '3', label: '2' }
      ],
      startNodeId: '1',
      flowName: 'Novo',
    }
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const current = pages.find((p) => p.id === currentPage);

  // histórico (undo/redo)
  const { commit, undo, redo } = useHistory(pages, setPages);

  // restauração de rascunho
  useEffect(() => {
    const draft = loadDraft();
    if (draft?.pages && window.confirm('Encontramos um rascunho salvo. Deseja restaurar?')) {
      // se vier gptDefaults no draft antigo, vamos ignorar silenciosamente
      const cleaned = draft.pages.map((pg) => {
        const { gptDefaults, ...rest } = pg || {};
        return rest;
      });
      setPages(cleaned);
      setCurrentPage(cleaned[0]?.id || 1);
    }
  }, []);

  // autosave
  const [dirty, setDirty] = useState(false);
  useAutosaveFlow(pages, setDirty);

  // inválidos para destaque
  const [invalidNodeIds, setInvalidNodeIds] = useState(new Set());
  useEffect(() => {
    const { invalidIds } = validatePages(pages);
    setInvalidNodeIds(invalidIds);
  }, [pages]);

  // UI e ReactFlow
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [showEditorPanel, setShowEditorPanel] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [editorExpandido, setEditorExpandido] = useState({
    open: false,
    nodeId: null,
    field: 'label',
    title: '',
    value: '',
    nodeType: 'TEXT',
  });
  
  const abrirEditorExpandido = ({
    nodeId,
    field = 'label',
    title = 'Editar conteúdo',
    value = '',
    nodeType = 'TEXT',
  }) => {
    setEditorExpandido({
      open: true,
      nodeId,
      field,
      title,
      value,
      nodeType,
    });
  };
  
  const fecharEditorExpandido = () => {
    setEditorExpandido((anterior) => ({
      ...anterior,
      open: false,
    }));
  };
  
  const salvarEditorExpandido = () => {
    const { nodeId, field, value } = editorExpandido;
  
    setCurrentPageData({
      nodes: current.nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                [field]: value,
              },
            }
          : node
      ),
    });
  
    setTimeout(() => updateNodeInternals(nodeId), 0);
    fecharEditorExpandido();
  };
  const inputFileRef = useRef(null);
  const updateNodeInternals = useUpdateNodeInternals();
  const applyGptPreset = (presetId) => {
    const preset = GPT_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    setCurrentPageData({
      nodes: current.nodes.map(n => {
        if (n.id !== selectedNodeId) return n;
        const before = n.data?.gpt || {};
        return { ...n, data: { ...n.data, gpt: { ...before, ...preset.config } } };
      })
    });
    setTimeout(() => updateNodeInternals(selectedNodeId), 0);
  };
  
  const [erroCampo, setErroCampo] = useState('');
  const reactFlow = useReactFlow();

  useEffect(() => setErroCampo(''), [selectedNodeId]);

  useEffect(() => {
    setTimeout(() => {
      if (reactFlow && typeof reactFlow.fitView === 'function') {
        reactFlow.fitView({ duration: 350 });
      }
    }, 120);
  }, [currentPage, current?.nodes?.length, reactFlow]);

  // helper write no "currentPage"
  const setCurrentPageData = (data) => {
    const nextPages = pages.map((p) =>
      p.id === currentPage ? { ...p, ...(typeof data === 'function' ? data(p) : data) } : p
    );
    commit(nextPages);
  };

  // ====== CLIPBOARD / ATALHOS ======
  const clipboardRef = useRef(null);
  const pasteBumpRef = useRef(0);

  const isTypingInInput = (e) => {
    const t = e.target;
    const tag = (t?.tagName || '').toLowerCase();
    const editable = t?.isContentEditable;
    return tag === 'input' || tag === 'textarea' || editable;
  };

  const getSelectedNodes = () => current.nodes.filter((n) => n.selected);
  const getSelectedEdges = () => current.edges.filter((e) => e.selected);

  const copySelection = () => {
    const selNodes = getSelectedNodes();
    if (selNodes.length === 0) return;
    const selIds = new Set(selNodes.map((n) => n.id));
    const selEdges = current.edges.filter((e) => selIds.has(e.source) && selIds.has(e.target));
    clipboardRef.current = {
      nodes: selNodes.map((n) => JSON.parse(JSON.stringify(n))),
      edges: selEdges.map((e) => JSON.parse(JSON.stringify(e))),
    };
    pasteBumpRef.current = 0;
  };

  const pasteClipboard = () => {
    if (!clipboardRef.current || !clipboardRef.current.nodes?.length) return;

    const idMap = new Map();
    const bump = 20 * (pasteBumpRef.current + 1);

    const newNodes = clipboardRef.current.nodes.map((orig) => {
      const newId = getNextGlobalId(pages);
      idMap.set(orig.id, newId);
      return {
        ...orig,
        id: newId,
        data: { ...orig.data, id: newId },
        position: { x: orig.position.x + bump, y: orig.position.y + bump },
        selected: false,
      };
    });

    const newEdges = clipboardRef.current.edges
      .map((orig) => {
        const src = idMap.get(orig.source);
        const tgt = idMap.get(orig.target);
        if (!src || !tgt) return null;
        return {
          ...orig,
          id: `e${src}-${tgt}-${Math.random().toString(36).slice(2, 7)}`,
          source: src,
          target: tgt,
          selected: false,
        };
      })
      .filter(Boolean);

    setCurrentPageData({
      nodes: [
        ...current.nodes.map((n) => ({ ...n, selected: false })),
        ...newNodes.map((n) => ({ ...n, selected: true })),
      ],
      edges: [...current.edges.map((e) => ({ ...e, selected: false })), ...newEdges],
    });

    pasteBumpRef.current += 1;
  };

  const deleteSelection = () => {
    const selNodes = new Set(getSelectedNodes().map((n) => n.id));
    const selEdges = new Set(getSelectedEdges().map((e) => e.id));
    if (selNodes.size === 0 && selEdges.size === 0) return;

    setCurrentPageData({
      nodes: current.nodes.filter((n) => !selNodes.has(n.id)),
      edges: current.edges.filter(
        (e) => !selEdges.has(e.id) && !selNodes.has(e.source) && !selNodes.has(e.target)
      ),
      startNodeId: selNodes.has(current.startNodeId) ? '' : current.startNodeId,
    });
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  };

  // atalhos
  useEffect(() => {
    const onKey = (e) => {
      if (isTypingInInput(e)) return;

      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();

      if (ctrl && key === 'c') { e.preventDefault(); copySelection(); return; }
      if (ctrl && key === 'v') { e.preventDefault(); pasteClipboard(); return; }
      if (ctrl && key === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if ((ctrl && key === 'y') || (ctrl && e.shiftKey && key === 'z')) { e.preventDefault(); redo(); return; }
      if (ctrl && key === 's') { e.preventDefault(); try { exportarJson(); } catch {} return; }
      if (key === 'delete' || key === 'backspace') { e.preventDefault(); deleteSelection(); return; }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, pages, undo, redo]);

  // ====== edição nó/edge ======
  const handleNodeDataChange = (field, value) => {
    setCurrentPageData({
      nodes: current.nodes.map((n) => {
        if (n.id !== selectedNodeId) return n;
        if (n.data.type === 'FILE' && field === 'caminho') {
          return { ...n, data: { ...n.data, caminho: value, label: value } };
        }
        if (n.data.type === 'ELO' && field === 'destino') {
          return { ...n, data: { ...n.data, destino: value === '' ? '' : value } };
        }
        return { ...n, data: { ...n.data, [field]: value } };
      }),
    });
    setTimeout(() => updateNodeInternals(selectedNodeId), 0);
  };

  const handleNodeDataPath = (path, value) => {
    setCurrentPageData({
      nodes: current.nodes.map((n) => {
        if (n.id !== selectedNodeId) return n;
        const clone = structuredClone(n);
        const parts = path.split('.');
        let ref = clone.data || (clone.data = {});
        for (let i = 0; i < parts.length - 1; i++) {
          ref[parts[i]] = ref[parts[i]] ?? {};
          ref = ref[parts[i]];
        }
        ref[parts.at(-1)] = value;
        return clone;
      }),
    });
    setTimeout(() => updateNodeInternals(selectedNodeId), 0);
  };

  const handleLembreteChange = (campo, valor) => {
    setCurrentPageData({
      nodes: current.nodes.map((n) =>
        n.id === selectedNodeId
          ? { ...n, data: { ...n.data, lembrete: { ...((n.data && n.data.lembrete) || {}), [campo]: valor } } }
          : n
      ),
    });
    setTimeout(() => updateNodeInternals(selectedNodeId), 0);
  };
  const handleLembreteAtivoChange = (checked) => {
    setCurrentPageData({
      nodes: current.nodes.map((n) =>
        n.id === selectedNodeId
          ? { ...n, data: { ...n.data, lembrete: checked ? { msg: '', tempo: '' } : undefined } }
          : n
      ),
    });
    setTimeout(() => updateNodeInternals(selectedNodeId), 0);
  };

  // estilos nodes
  function getBorderColor(n) {
    if (n.id === current.startNodeId) return 'green';
    if (n.data.type === 'GPT') return '#0055CC';
    if (n.data.type === 'FILE') return '#B22222';
    if (n.data.type === 'CAPTURA') return '#DE911D';
    if (n.data.type === 'ELO') return '#004080';
    if (n.data.type === 'ATENDIMENTO') return '#8800CC';
    if (n.data.type === 'CADASTRAR') return '#14631d';
    if (n.data.type === 'EXCLUIR') return '#c01414';
    if (n.data.type === 'STOP') return '#880000';
    if (n.data.type === 'VCARD') return '#2b8c2b';
    if (n.data.type === 'ROUTER') return '#004080';
    return '#555';
  }
  function getBorderWidth(n) { return n.id === selectedNodeId ? 4 : 2; }
  function hasReminder(n) {
    return (
      !!n.data.lembrete &&
      n.data.type !== 'STOP' &&
      n.data.type !== 'ELO' &&
      n.data.type !== 'CADASTRAR' &&
      n.data.type !== 'EXCLUIR' &&
      n.data.type !== 'ROUTER'
    );
  }

  const limitarLinhas = (texto, maxLinhas = 5) => {
    if (!texto) return '';
    const linhas = texto.split('\n');
    if (linhas.length <= maxLinhas) return texto;
    return linhas.slice(0, maxLinhas).join('\n') + '\n...';
  };

  const styledNodes = current.nodes.map((n) => {
    const isInvalid = invalidNodeIds.has(String(n.id));
    const isStart = String(n.id) === String(current.startNodeId);

    let contentLines = [];
    if (n.data.type === 'VCARD') {
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3 }}>
          {n.data.nome ? n.data.nome : <span style={{ color: '#aaa' }}>Preencha o nome...</span>}
        </div>
      );
      contentLines.push(
        <div style={{ fontSize: 14, color: '#444' }}>
          {n.data.telefone ? n.data.telefone : <span style={{ color: '#aaa' }}>Preencha o telefone...</span>}
        </div>
      );
    } else if (n.data.type === 'FILE') {
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3 }}>
          {n.data.caminho ? (
            limitarLinhas(n.data.caminho, 5)
          ) : (
            <span style={{ color: '#aaa' }}>Preencha o caminho do arquivo...</span>
          )}
        </div>
      );
      if (n.data.conteudo) {
        contentLines.push(
          <div style={{ fontSize: 14, color: '#444', marginTop: 4, whiteSpace: 'pre-line' }}>
            {limitarLinhas(n.data.conteudo, 5)}
          </div>
        );
      }
    } else if (n.data.type === 'GPT') {
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3, whiteSpace: 'pre-line' }}>
          {n.data.label && n.data.label.trim() ? (
            limitarLinhas(n.data.label, 5)
          ) : (
            <span style={{ color: '#aaa', fontStyle: 'italic' }}>{GPT_PLACEHOLDER}</span>
          )}
        </div>
      );
    } else if (n.data.type === 'EXCLUIR' || n.data.type === 'CADASTRAR') {
      const texto =
        n.data.label && n.data.label.trim()
          ? limitarLinhas(n.data.label, 5)
          : (n.data.type === 'EXCLUIR'
              ? 'Guardar contato em lista de exclusão'
              : 'Guardar contato em lista de contatos');
    
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3, color: '#818' }}>
          {texto}
        </div>
      );
    } else if (n.data.type === 'CAPTURA') {
      const lbl = String(n?.data?.label || '').trim();
      if (lbl) {
        contentLines.push(
          <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3, whiteSpace: 'pre-line' }}>
            {limitarLinhas(lbl, 5)}
          </div>
        );
      }
    
      // Badge de campo — SOMENTE na CAPTURA
      const campo = String(n?.data?.capturaCampo || '').trim();
      contentLines.push(
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: campo ? '#0f6c5a' : '#d32f2f',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <InputIcon sx={{ fontSize: 16 }} />
          <span>Campo:</span>
          <span
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              padding: '0 6px',
            }}
          >
            {campo ? `{${campo}}` : ''}
          </span>
        </div>
      );
    } else if (n.data.type === 'ROUTER') {
      contentLines.push(
        <div style={{ fontSize: 14, color: '#444', marginTop: 4 }}>
          Roteia sem enviar mensagem. Use labels: =EXATO (ex.: =2,SIM,S) e ~CONTÉM (ex.: ~SIM).
        </div>
      );
    } else {
      // Demais tipos: só o conteúdo
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3, whiteSpace: 'pre-line' }}>
          {n.data.label && n.data.label.trim()
            ? limitarLinhas(n.data.label, 5)
            : <span style={{ color: '#aaa' }}>Preencha o conteúdo...</span>}
        </div>
      );
    }
    
    const showIdBadge = !['ELO', 'STOP', 'RETURN'].includes(n.data.type);
    contentLines.unshift(
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
          fontSize: 16,
          fontWeight: 700,
          minHeight: 28,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {showIdBadge && (
            <span
              style={{
                fontSize: 12,
                color: '#888',
                background: '#fff',
                borderRadius: 4,
                padding: '1px 7px',
                border: '1px solid #ddd',
                fontWeight: 500,
                letterSpacing: 1,
                minWidth: 28,
                textAlign: 'center',
              }}
            >
              #{n.id}
            </span>
          )}
          {NODE_ICONS[n.data.type] || <MessageIcon sx={{ color: '#555' }} fontSize="small" />}
          <span style={{ marginLeft: 4 }}>{NODE_LABELS[n.data.type] || 'Etapa'}</span>
        </div>
        {hasReminder(n) && (
          <Tooltip title="Esta etapa tem lembrete">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <FaBell style={{ color: '#FFA500', fontSize: 17 }} />
            </span>
          </Tooltip>
        )}
        {isInvalid && (
          <Tooltip title="Preenchimento obrigatório ausente">
            <span style={{ fontSize: 12, color: '#d32f2f', fontWeight: 700 }}>⚠️</span>
          </Tooltip>
        )}
      </div>
    );

    if (['ELO', 'STOP', 'RETURN'].includes(n.data.type)) {
      let cor = '#004080'; // ELO
      let texto = n.data.type === 'ELO' ? (n.data.destino && n.data.destino !== '' ? n.data.destino : '—') : n.data.type;
      if (n.data.type === 'STOP') cor = '#880000';
      if (n.data.type === 'RETURN') cor = '#1976d2';

      return {
        ...n,
        style: {
          border: `3px solid ${isInvalid ? '#d32f2f' : cor}`,
          background: isInvalid
            ? 'linear-gradient(120deg, #ffecec 30%, #fff 100%)'
            : 'linear-gradient(120deg, #f2f7ff 30%, #fff 100%)',
          borderRadius: '50%',
          width: 60,
          minHeight: 60,
          height: 60,
          fontWeight: 'bold',
          fontSize: '0.50rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          userSelect: 'none',
          color: cor,
          textTransform: 'uppercase',
        },
        data: { ...n.data, label: <div>{texto}</div> },
      };
    }

    // --- Rodapé: WP (todas as rotinas, exceto ELO/RETURN/STOP) ---
    const wpTexto = String(n?.data?.wp || '').trim();
    if (wpTexto && !['ELO','RETURN','STOP','ROUTER'].includes(n.data.type)) {
      contentLines.push(
        <div
          style={{
            marginTop: 8,
            paddingTop: 6,
            borderTop: '1px dashed #b7e3dc',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: '#0f6c5a',
          }}
        >
          <MapIcon sx={{ fontSize: 16, color: '#0f6c5a' }} />
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            WP: {wpTexto}
          </span>
        </div>
      );
    }


    const bgGradient =
    n.id === selectedNodeId
      ? 'linear-gradient(120deg, #e0ffe0 30%, #fff 90%)'
      : isStart
        ? 'linear-gradient(120deg, #b4f5a0 30%, #fff 100%)' // rgb(180,245,160) em hex
        : 'linear-gradient(120deg, #f2f7ff 30%, #fff 100%)';
  
    return {
      ...n,
      style: {
        border: `${getBorderWidth(n)}px solid ${isInvalid ? '#d32f2f' : getBorderColor(n)}`,
        background: isInvalid
          ? 'linear-gradient(120deg, #ffecec 30%, #fff 90%)'
          : bgGradient,
        borderRadius: '12px',
        width: 220,
        minHeight: 64,
        maxWidth: 250,
        fontSize: '1rem',
        display: 'block',
        textAlign: 'left',
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        overflow: 'hidden',
        padding: '10px 14px',
        boxSizing: 'border-box',
        boxShadow: n.id === selectedNodeId ? '0 0 0 2px #66ff33, 0 3px 12px rgba(0,0,0,0.08)' : '0 1px 6px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.3s, background 0.3s, border 0.3s',
      },
      data: { ...n.data, label: <div>{contentLines}</div> },
    };
  });

  // conexões
  const onConnect = useCallback((params) => {
    const nodes = current.nodes;
    const edges = current.edges;
    const sourceNode = nodes.find(n => n.id === params.source);
    const targetNode = nodes.find(n => n.id === params.target);
    const tipo = (sourceNode?.data?.type || "").toUpperCase();
  
    const isSamePair = edges.some(e =>
      (e.source === params.source && e.target === params.target) ||
      (e.source === params.target && e.target === params.source)
    );
    if (isSamePair) {
      alert("❌ Já existe uma ligação entre essas etapas (mesmo par).");
      return;
    }
  
    if (params.source === params.target) {
      alert("❌ Não é permitido ligar uma etapa nela mesma.");
      return;
    }
  
    if (tipo === "ELO") { alert("❌ ELO não pode ter saída."); return; }
    if (tipo === "STOP") { alert("❌ STOP não pode ter saída."); return; }
    if (tipo === "CAPTURA") {
      const saidas = edges.filter(e => e.source === sourceNode.id);
      if (saidas.length >= 1) { alert("❌ CAPTURA só pode ter uma saída."); return; }
    }
    if (tipo === "GPT" && (targetNode?.data?.type || "").toUpperCase() === "ATENDIMENTO") {
      alert("❌ GPT não pode direcionar para um ATENDIMENTO."); return;
    }
  
    // *** MODAL PARA DIGITAR O LABEL ***
    let label = "";
    if (tipo !== "CAPTURA") { // CAPTURA é sempre automática (sem label)
      const promptMessage = [
        "Prefixos do label (separe múltiplos por vírgula):",
        "  =EXATO   → '=2,SIM,S'  (casa apenas se for exatamente 2, SIM ou S)",
        "  ~CONTÉM  → '~SIM,NAO'  (casa se a mensagem CONTIVER SIM ou NAO)",
        "Sem prefixo → trata como EXATO",
        "",
        "* = curinga (fallback); vazio = segue automático"
      ].join("\n");
      label = (prompt(promptMessage, "") ?? "")
        .trim()
        .replace(/\s*,\s*/g, ",");
    }
  
    if (edges.some(e => e.source === params.source && e.target === params.target && (e.label || "") === label)) {
      alert("Ligação duplicada!"); return;
    }
  
    setCurrentPageData({ edges: addEdge({ ...params, label }, edges) });
  }, [current]);
    
  // eventos reactflow
  const onNodesChange = useCallback(
    (changes) => { setCurrentPageData({ nodes: applyNodeChanges(changes, current.nodes) }); },
    [current]
  );
  const onEdgesChange = useCallback(
    (changes) => { setCurrentPageData({ edges: applyEdgeChanges(changes, current.edges) }); },
    [current]
  );

  // criar nodes
  const addNewNode = useCallback(
    (type = 'TEXT') => {
      const id = getNextGlobalId(pages);
      let newNode;
      if (type === 'CAPTURA') {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, label: '', type: 'CAPTURA', capturaCampo: '' } };
      } else if (type === 'ELO') {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, label: '', type: 'ELO', destino: '', retorno: '' } };
      } else if (type === 'RETURN') {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, type: 'RETURN', label: '' } };
      } else if (type === 'STOP') {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, label: 'STOP', type: 'STOP' } };
      } else if (type === 'FILE') {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, label: '', type: 'FILE', caminho: '', conteudo: '' } };
      } else if (type === 'GPT') {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, label: '', type: 'GPT', gpt: { ...DEFAULT_GPT_NODE } } };
      } else if (type === 'ATENDIMENTO') {
        newNode = {
          id,
          type: 'default',
          position: { x: Math.random() * 600, y: Math.random() * 400 },
          data: {
            id,
            label: '📞 Novo Atendimento, vamos transferir o seu atendimento, aguarde...',
            type: 'ATENDIMENTO',
            numeroAtendimentoDestino: '',
          },
        };
      } else if (type === 'CADASTRAR') {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, label: '', type: 'CADASTRAR' } };
      } else if (type === 'EXCLUIR') {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, label: '', type: 'EXCLUIR' } };
      } else if (type === 'VCARD') {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, type: 'VCARD', nome: '', telefone: '' } };
      } else if (type === 'ROUTER') { 
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, type: 'ROUTER', label: '' } };
      } else {
        newNode = { id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 }, data: { id, label: '', type } };
      }
      setCurrentPageData({ nodes: [...current.nodes, newNode] });
    },
    [current, pages]
  );

  const handleStartChange = useCallback(() => {
    if (!selectedNodeId) return;
    // INÍCIO global: mantém apenas na página atual
    commit(pages.map(pg =>
      pg.id === currentPage
        ? { ...pg, startNodeId: selectedNodeId }
        : { ...pg, startNodeId: '' }
    ));
  }, [selectedNodeId, pages, currentPage, commit]);

  const deleteSelectedNode = useCallback(() => {
    setCurrentPageData({
      nodes: current.nodes.filter((n) => n.id !== selectedNodeId),
      edges: current.edges.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId),
      startNodeId: current.startNodeId === selectedNodeId ? '' : current.startNodeId,
    });
    setSelectedNodeId(null);
  }, [current, selectedNodeId]);

  const onArquivoSelecionado = useCallback(e => {
    const file = e.target.files[0];
    if (!file) return;
    const nomeArquivo = file.name ? file.name.replace(/\.json$/i, '') : '';
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        let j = JSON.parse(ev.target.result);
        if (Array.isArray(j.pages)) {
          setPages(j.pages);
          setCurrentPage(j.pages[0]?.id || 1);
        } else if (j.nodes && j.edges) {
          setPages([{
            id: 1, name: nomeArquivo || 'Página 1',
            nodes: j.nodes, edges: j.edges, startNodeId: j.startNodeId || '',
            flowName: j.nome && String(j.nome).trim() !== '' ? j.nome : nomeArquivo
          }]);
          setCurrentPage(1);
        } else {
          throw new Error('Formato de arquivo inválido');
        }
        setSelectedNodeId(null);
      } catch {
        alert('Arquivo inválido');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);

  const exportarJson = () => {
    const result = validatePages(pages);
    if (!result.ok) {
      const linhas = [];
      const byPage = new Map();
      for (const err of result.errors) {
        const key = `${err.pageId}::${err.pageName || ''}`;
        if (!byPage.has(key)) byPage.set(key, []);
        byPage.get(key).push(err);
      }
      for (const [key, arr] of byPage.entries()) {
        const [, pageName] = key.split('::');
        linhas.push(`Página: ${pageName || '(sem nome)'}`);
        for (const e of arr) {
          linhas.push(`  - Etapa #${e.nodeId} (${e.type}): ${e.reason}`);
        }
      }
      alert(
        'Não foi possível exportar.\n\nCorrija os campos obrigatórios:\n\n' +
        linhas.join('\n')
      );
      const first = result.errors[0];
      if (first) {
        const pg = pages.find(p => p.id === first.pageId);
        if (pg && pg.id !== currentPage) setCurrentPage(pg.id);
        setSelectedNodeId(String(first.nodeId));
        setSelectedEdgeId(null);
        setShowEditorPanel(true);
      }
      return;
    }
    const blob = new Blob([JSON.stringify({ pages }, null, 2)], { type: 'application/json' });
    saveAs(blob, `fluxo-multipagina.json`);
  };

  // campos variáveis
  function renderCamposVariaveis(nodes, labelField, handleChange, nodeType) {
    if (['ELO', 'VCARD', 'STOP', 'ROUTER'].includes(nodeType)) return null;
    const camposCaptura = listarCamposCapturados(pages);
    const camposSistema = CAMPOS_SISTEMA;
    if (camposCaptura.length === 0 && camposSistema.length === 0) return null;
    const renderBotaoCampo = (campo) => (
      <Button
        key={campo}
        size="small"
        sx={{ minWidth: 0, mx: 0.5, my: 0.5, p: '2px 6px', fontSize: 13, color: '#1a237e', border: '1px solid #aaa', borderRadius: 1, background: '#fff' }}
        onClick={() => {
          const insert = `{${campo}}`;
          const active = document.activeElement;
          if (active && active.tagName === 'TEXTAREA') {
            const textarea = active;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const before = (labelField || '').slice(0, start);
            const after = (labelField || '').slice(end);
            handleChange(before + insert + after);
            setTimeout(() => {
              textarea.focus();
              textarea.setSelectionRange(start + insert.length, start + insert.length);
            }, 10);
          } else {
            handleChange((labelField || '') + insert);
          }
        }}
      >
        {`{${campo}}`}
      </Button>
    );
    return (
      <Box sx={{ mb: 2, mt: -1.5 }}>
        {camposCaptura.length > 0 && (
          <>
            <b style={{ fontSize: 13 }}>Campos capturados:</b>
            <br />
            {camposCaptura.map(renderBotaoCampo)}
            <Divider sx={{ my: 1 }} />
          </>
        )}
        {camposSistema.length > 0 && (
          <>
            <b style={{ fontSize: 13 }}>Campos do sistema:</b>
            <br />
            {camposSistema.map(renderBotaoCampo)}
          </>
        )}
      </Box>
    );
  }

  /* ===== MAPA state (para GPT) ===== */
  const [mapOpen, setMapOpen] = useState(false);

  const openMapFromGpt = () => setMapOpen(true);
  const useMapPrompt = (texto) => {
    // cola o texto no label do GPT
    handleNodeDataChange('label', texto);
    setMapOpen(false);
  };

  if (verificandoChave) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <h2>Verificando chave de acesso...</h2>
      </Box>
    );
  }
  if (!podeUsarFlow) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <h2 style={{ color: 'red' }}>Acesso não autorizado</h2>
        <p>
          Esta chave não permite acesso ao Flow Editor.
          <br />
          Verifique se a chave está correta ou se possui permissão.
        </p>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Tentar novamente
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>

      {/* TOPO FIXO: Abas + Barra */}
      <Paper
      elevation={0}
      sx={{
        position: "sticky",
        top: 0,                         // ajuste se houver AppBar
        zIndex: (t) => t.zIndex.appBar + 1,
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        backdropFilter: "saturate(180%) blur(6px)",
      }}
      >
        {/* Abas de páginas */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: '1px solid #ccc', bgcolor: '#f1f1f1' }}>
          {pages.map((p) => (
            <Box
              key={p.id}
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: 1,
                bgcolor: p.id === currentPage ? '#fff' : '#e5e5e5',
                border: '1px solid #ccc',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
              }}
              onClick={() => setCurrentPage(p.id)}
            >
              <span>{p.name}</span>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const nm = prompt('Novo nome da página:');
                  if (nm && nm.trim()) commit(pages.map((pg) => (pg.id === p.id ? { ...pg, name: nm } : pg)));
                }}
              >
                ✏️
              </Button>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (pages.length === 1) {
                    alert('Não é possível excluir a única página!');
                    return;
                  }
                  if (!window.confirm('Excluir esta página?')) return;
                  const rem = pages.filter((pg) => pg.id !== p.id);
                  commit(rem);
                  setCurrentPage(rem[0].id);
                }}
              >
                🗑️
              </Button>
            </Box>
          ))}
          <Button
            startIcon={<span>➕</span>}
            onClick={() => {
              const newId = Math.max(...pages.map((p) => p.id)) + 1 || 2;
              commit((prev) => [
                ...prev,
                {
                  id: newId,
                  name: `Página ${newId}`,
                  nodes: [],
                  edges: [],
                  startNodeId: '',
                  flowName: `Página ${newId}`,
                },
              ]);
              setCurrentPage(newId);
            }}
          >
            Nova Página
          </Button>

          <Box sx={{ ml: 1, fontSize: 12, color: dirty ? 'text.secondary' : 'success.main' }}>
            {dirty ? 'Salvando...' : 'Rascunho salvo'}
          </Box>
        </Box>

        {/* Barra de ações */}
        <Box
          sx={{
            p: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexWrap: 'wrap',
            boxShadow: '0 3px 12px 0 rgba(60,100,180,0.03)',
          }}
        >
          <Tooltip title="Importar fluxo (JSON ou .enc.json)">
            <Button sx={{ bgcolor: '#fff', color: '#172554', fontWeight: 700, border: '1.5px solid #e0e0e0' }} startIcon={<DownloadIcon />} onClick={() => inputFileRef.current.click()}>
              Importar JSON
            </Button>
          </Tooltip>
          <Tooltip title="Exportar fluxo">
            <Button sx={{ bgcolor: '#fff', color: '#172554', fontWeight: 700, border: '1.5px solid #e0e0e0' }} startIcon={<ShareIcon />} onClick={exportarJson}>
              Exportar JSON
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#e0e0e0' }} />

          <Tooltip title="Ajuda e exemplos (somente leitura)">
            <IconButton
              aria-label="Abrir ajuda"
              onClick={() => setHelpOpen(true)}
              sx={{
                border: '1.5px solid #e0e0e0',
                bgcolor: '#fff',
                color: '#172554',
                width: 40,
                height: 36,
                borderRadius: '10px', // use '50%' se quiser circular
                '&:hover': { bgcolor: '#f8fafc' },
              }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#e0e0e0' }} />

          <Tooltip title="Adicionar nova etapa de mensagem">
            <Button sx={{ bgcolor: '#E53935', color: '#fff', fontWeight: 700, border: '1.5px solid #B71C1C' }} startIcon={<AddIcon />} onClick={() => addNewNode('TEXT')}>
              Nova Etapa
            </Button>
          </Tooltip>
          <Tooltip title="Criar ligação para outra etapa (ELO)">
            <Button sx={{ bgcolor: '#EF9A9A', color: '#fff', fontWeight: 700, border: '1.5px solid #B71C1C' }} startIcon={<AccountTreeIcon />} onClick={() => addNewNode('ELO')}>
              ELO
            </Button>
          </Tooltip>
          <Tooltip title="Etapa de retorno">
            <Button sx={{ bgcolor: '#EF9A9A', color: '#fff', fontWeight: 700, border: '1.5px solid #B71C1C' }} startIcon={<ReplayIcon />} onClick={() => addNewNode('RETURN')}>
              RETURN
            </Button>
          </Tooltip>
          <Tooltip title="Inserir etapa de parada">
            <Button sx={{ bgcolor: '#EF9A9A', color: '#fff', fontWeight: 700, border: '1.5px solid #B71C1C' }} startIcon={<StopCircleIcon />} onClick={() => addNewNode('STOP')}>
              STOP
            </Button>
          </Tooltip>
          <Tooltip title="Roteia sem enviar mensagem">
            <Button
              sx={{ bgcolor: '#EF9A9A', color: '#fff', fontWeight: 700, border: '1.5px solid #B71C1C' }}
              startIcon={<AccountTreeIcon />}
              onClick={() => addNewNode('ROUTER')}
            >
              Router
            </Button>
          </Tooltip>
        
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#e0e0e0' }} />

          <Tooltip title="Enviar arquivo no fluxo">
            <Button sx={{ bgcolor: '#FF9800', color: '#fff', fontWeight: 700, border: '1.5px solid #F57C00' }} startIcon={<InsertDriveFileIcon />} onClick={() => addNewNode('FILE')}>
              FILE
            </Button>
          </Tooltip>
          <Tooltip title="Enviar contato no padrão vCard">
            <Button sx={{ bgcolor: '#FF9800', color: '#fff', fontWeight: 700, border: '1.5px solid #F57C00' }} startIcon={<ContactPhoneIcon />} onClick={() => addNewNode('VCARD')}>
              vCARD
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#e0e0e0' }} />

          <Tooltip title="Capturar informação digitada pelo usuário">
            <Button sx={{ bgcolor: '#FFF9C4', color: '#000', fontWeight: 700, border: '1.5px solid #FBC02D' }} startIcon={<NoteAddIcon />} onClick={() => addNewNode('CAPTURA')}>
              Capturar
            </Button>
          </Tooltip>
          <Tooltip title="Cadastrar o usuário como contato">
            <Button sx={{ bgcolor: '#FFF9C4', color: '#000', fontWeight: 700, border: '1.5px solid #FBC02D' }} startIcon={<AssignmentIndIcon />} onClick={() => addNewNode('CADASTRAR')}>
              Cadastrar Contato
            </Button>
          </Tooltip>
          <Tooltip title="Excluir o contato do usuário">
            <Button sx={{ bgcolor: '#FFF9C4', color: '#000', fontWeight: 700, border: '1.5px solid #FBC02D' }} startIcon={<PersonRemoveIcon />} onClick={() => addNewNode('EXCLUIR')}>
              Excluir Contato
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#e0e0e0' }} />

          <Tooltip title="Transferir o atendimento para outro número">
            <Button sx={{ bgcolor: '#90caf9', color: '#0d1330', fontWeight: 700, border: '1.5px solid #64b5f6' }} startIcon={<PhoneAndroidIcon />} onClick={() => addNewNode('ATENDIMENTO')}>
              Trocar Atendimento
            </Button>
          </Tooltip>
          <Tooltip title="Etapa de resposta com Inteligência Artificial">
            <Button sx={{ bgcolor: '#90caf9', color: '#0d1330', fontWeight: 700, border: '1.5px solid #64b5f6' }} startIcon={<SmartToyIcon />} onClick={() => addNewNode('GPT')}>
              GPT
            </Button>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
        </Box>
      </Paper>
      {/* Canvas ReactFlow */}
      <Box sx={{ flexGrow: 1, position: 'relative', background: '#f9f9f9' }}>
        <input ref={inputFileRef} type="file" accept=".json,.enc.json, application/json" style={{ display: 'none' }} onChange={onArquivoSelecionado} />

        <ReactFlow
          nodes={styledNodes}
          edges={current.edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          panOnDrag
          selectionOnDrag={false}
          selectionKeyCode="Shift"
          multiSelectionKeyCode="Control"
          selectionMode={SelectionMode.Partial}
          onNodeClick={(e, n) => {
            setSelectedNodeId(n.id);
            setSelectedEdgeId(null);
            setShowEditorPanel(true);
          }}
          onNodeDoubleClick={(e, n) => {
            if (n.data.type === 'ELO') {
              const destinoId = n.data.destino;
              if (!destinoId) return;
              const paginaDoDestino = pages.find((p) => p.nodes.some((nd) => nd.id === destinoId));
              if (paginaDoDestino) {
                if (paginaDoDestino.id !== currentPage) {
                  setCurrentPage(paginaDoDestino.id);
                  setTimeout(() => {
                    setSelectedNodeId(destinoId);
                    setSelectedEdgeId(null);
                    setShowEditorPanel(true);
                  }, 50);
                } else {
                  setSelectedNodeId(destinoId);
                  setSelectedEdgeId(null);
                  setShowEditorPanel(true);
                }
              } else {
                setSelectedNodeId(n.id);
                setSelectedEdgeId(null);
                setShowEditorPanel(true);
              }
            } else {
              setSelectedNodeId(n.id);
              setSelectedEdgeId(null);
              setShowEditorPanel(true);
            }
          }}
          onEdgeDoubleClick={(e, edge) => {
            e.stopPropagation();
            setSelectedEdgeId(edge.id);
            setSelectedNodeId(null);
            setShowEditorPanel(true);
          }}
          onEdgeClick={(e, edge) => {
            e.stopPropagation();
            setSelectedEdgeId(edge.id);
            setSelectedNodeId(null);
            setShowEditorPanel(true);
          }}
          onPaneClick={() => {
            setSelectedNodeId(null);
            setSelectedEdgeId(null);
            setShowEditorPanel(false);
          }}
          fitView
          deleteKeyCode={['Backspace', 'Delete']}
          style={{ height: '100%' }}
        >
          <Box
            sx={{
              '& .react-flow__controls-button': { width: 56, height: 56, fontSize: '2rem' },
              '& .react-flow__controls-button svg': { width: '2em', height: '2em' },
            }}
          >
            <Controls position="top-left" showInteractive />
          </Box>

          <MiniMap position="bottom-left" style={{ border: '1px solid #000', borderRadius: 4, background: 'rgba(255,255,255,0.85)' }} />

          <MapDialog
            open={mapOpen}
            onClose={() => setMapOpen(false)}
            onUse={useMapPrompt}
            pages={pages}
          />
        </ReactFlow>

        {showEditorPanel && selectedEdgeId && (() => {
          const edgeSel = current.edges.find((e) => e.id === selectedEdgeId);
          const sourceNode = current.nodes.find((n) => n.id === edgeSel?.source);
          const sourceType = (sourceNode?.data?.type || '').toUpperCase();
          const labelReadOnly = sourceType === 'CAPTURA';

          const helper = labelReadOnly
            ? 'Esta ligação é automática para CAPTURA; deixe o label vazio.'
            : 'Use = para EXATO (ex.: =2,SIM,S) e ~ para CONTÉM (ex.: ~SIM). Sem prefixo = EXATO.';
        
          return (

            <Box
              component="aside"
              sx={{ width: 360, p: 2, borderLeft: 1, borderColor: 'divider', bgcolor: 'grey.50', position: 'absolute', right: 0, top: 0, height: '100%', zIndex: 10, }}
            >
              <Button
                  onClick={() => setShowEditorPanel(false)}
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<VisibilityOffIcon />}
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    letterSpacing: 1,
                    py: 1.3,
                    bgcolor: '#1976d2',
                    color: '#fff',
                    '&:hover': { bgcolor: '#1565c0' },
                  }}
                >
                  Ocultar edição
              </Button>

              <TextField
                label="Ligação"
                value={`${edgeSel?.source ?? '?'} → ${edgeSel?.target ?? '?'}`}
                InputProps={{ readOnly: true, sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } }}
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
              />

              {/* Bloco de regras bonito, sem TextField vazio */}
              <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Regras da ligação
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, lineHeight: 1.45, fontSize: 12, color: 'text.secondary' }}>
                <li><strong>=</strong> EXATO — ex.: <code>=2,SIM,S</code></li>
                <li><strong>~</strong> CONTÉM — ex.: <code>~SIM,NAO</code> (casa “claro que <i>sim</i>”)</li>
                <li>Sem prefixo → trata como <em>EXATO</em></li>
                <li><strong>*</strong> curinga | <strong>vazio</strong> segue automático</li>
                <li>Múltiplos valores separados por vírgula</li>
                </Box>
              </Paper>

              {/* Label da ligação */}
              <TextField
                label="Label da ligação"
                value={edgeSel?.label || ''}
                onChange={(e) => {
                  if (labelReadOnly) return;
                  const newLabel = e.target.value;
                  setCurrentPageData({
                    edges: current.edges.map((edge) =>
                      edge.id === selectedEdgeId ? { ...edge, label: newLabel } : edge
                    ),
                  });
                }}

                placeholder={labelReadOnly ? '(automática — deixe vazio)' : 'Ex.: =2,SIM,S  |  ~SIM  |  *  |  (vazio)'}
                InputProps={{ readOnly: labelReadOnly }}
                helperText={helper}
                fullWidth
                sx={{ mb: 1.5 }}
              />
                    
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={() => {
                  setCurrentPageData({ edges: current.edges.filter((edge) => edge.id !== selectedEdgeId) });
                  setSelectedEdgeId(null);
                }}
              >
                Apagar ligação
              </Button>
            </Box>
          );
        })()}
        
        {/* Sidebar Edição Nó */}
        {showEditorPanel && selectedNodeId && current.nodes.find((n) => n.id === selectedNodeId) && (() => {
          const selectedNode = current.nodes.find((n) => n.id === selectedNodeId);
          const isCadastrar = selectedNode?.data.type === 'CADASTRAR';
          const isExcluir = selectedNode?.data.type === 'EXCLUIR';
          const showWp = !['ELO','RETURN','STOP','ROUTER'].includes(selectedNode?.data.type);

          return (
            <Box
              component="aside"
              sx={{
                width: 360,
                p: 2,
                borderLeft: 1,
                borderColor: 'divider',
                bgcolor: 'grey.50',
                position: 'absolute',
                right: 0,
                top: 0,
                height: '100%',
                zIndex: 10,
              }}
            >
              {/* ——— cabeçalho / botões ——— */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  onClick={() => setShowEditorPanel(false)}
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<VisibilityOffIcon />}
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    letterSpacing: 1,
                    py: 1.3,
                    bgcolor: '#1976d2',
                    color: '#fff',
                    '&:hover': { bgcolor: '#1565c0' },
                  }}
                >
                  Ocultar edição
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="ID da Etapa"
                  value={selectedNodeId}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  sx={{ mb: 0, flex: 1 }}
                />
                <TextField
                  label="Tipo"
                  value={selectedNode?.data.type || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  sx={{ mb: 0, flex: 1 }}
                />
              </Box>

              {showWp && <Divider sx={{ my: 2 }} />}

              {showWp && (
                <TextField
                  label="Way-Point"
                  size="small"
                  fullWidth
                  value={selectedNode?.data?.wp || ''}
                  onChange={(e) => handleNodeDataChange('wp', e.target.value)}
                  placeholder="Ex.: suporte, vendas, financeiro, cobrança…"
                  helperText="Digite a palavra que identifica este caminho"
                />
              )}
            
              <Divider sx={{ my: 2 }} />

              {selectedNode?.data.type === 'ROUTER' && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Este nó não envia mensagem. Ele apenas direciona de acordo com as arestas.
                </Typography>
              )}
              

              {/* ---- BLOCO: Configurações GPT por nó ---- */}
              {selectedNode?.data.type === 'GPT' && (
                <>
                  <Box sx={{ p: 1, mb: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {/* --- Agrupar e desenhar cada opção com explicação --- */}
                    {/* Modelo (via modal bonitinho) */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.2 }}>
                      <TextField
                        label="Model"
                        size="small"
                        value={selectedNode?.data?.gpt?.model ?? ''}
                        InputProps={{ readOnly: true }}
                        fullWidth
                      />
                      <Button variant="outlined" onClick={() => setModelDialogOpen(true)}>
                        Selecionar
                      </Button>
                    </Box>

                    <ModelPickerDialog
                      open={modelDialogOpen}
                      currentModel={selectedNode?.data?.gpt?.model ?? ''}
                      onClose={() => setModelDialogOpen(false)}
                      onSelect={(modelId, applyDefaults) => {
                        handleNodeDataPath('gpt.model', modelId);
                        if (applyDefaults) {
                          const d = MODEL_CATALOG[modelId]?.defaults;
                          if (d) {
                            handleNodeDataPath('gpt.temperature', d.temperature);
                            handleNodeDataPath('gpt.maxTokens', d.maxTokens);
                            handleNodeDataPath('gpt.sendHistory', d.sendHistory);
                            handleNodeDataPath('gpt.historyLimit', d.historyLimit);
                          }
                        }
                        setModelDialogOpen(false);
                      }}
                    />

                    {/* Presets rápidos */}
                    <Box sx={{ mb: 1.2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Presets rápidos
                    </Typography>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {GPT_PRESETS.map(p => (
                        <Tooltip key={p.id} title={p.desc}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => applyGptPreset(p.id)}
                            sx={{ textTransform: 'none' }}
                          >
                            {p.short}
                          </Button>
                        </Tooltip>
                      ))}
                    </Stack>
                    </Box>
                    <Divider sx={{ my: 1.2 }} />

                    {/* Temperature e MaxTokens */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.2 }}>
                      <TextField
                        label="Temperature (0–2)"
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{ step: 0.1, min: 0, max: 2 }}
                        value={selectedNode?.data?.gpt?.temperature ?? DEFAULT_GPT_NODE.temperature}
                        onChange={(e) => handleNodeDataPath('gpt.temperature', Number(e.target.value))}
                      />
                      <TextField
                        label="MaxTokens"
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{ min: 1 }}
                        value={selectedNode?.data?.gpt?.maxTokens ?? DEFAULT_GPT_NODE.maxTokens}
                        onChange={(e) => handleNodeDataPath('gpt.maxTokens', parseInt(e.target.value || '0', 10))}
                      />
                    </Box>
                    <Button
                      startIcon={<MapIcon />}
                      variant="outlined"
                      onClick={openMapFromGpt}
                      sx={{ mb: 1.2 }}
                    >
                      MAPA WAY-POINTS
                    </Button>
                    {/* Histórico */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedNode?.data?.gpt?.sendHistory ?? DEFAULT_GPT_NODE.sendHistory}
                          onChange={(e) => handleNodeDataPath('gpt.sendHistory', e.target.checked)}
                        />
                      }
                      label="Enviar histórico para o GPT"
                      sx={{ mb: 1 }}
                    />

                    {(selectedNode?.data?.gpt?.sendHistory ?? DEFAULT_GPT_NODE.sendHistory) && (
                      <TextField
                        label="History limit"
                        type="number"
                        size="small"
                        fullWidth
                        sx={{ mb: 1.2 }}
                        inputProps={{ min: 1, max: 50 }}
                        value={selectedNode?.data?.gpt?.historyLimit ?? DEFAULT_GPT_NODE.historyLimit}
                        onChange={(e) => handleNodeDataPath('gpt.historyLimit', parseInt(e.target.value || '0', 10))}
                      />
                    )}
                  </Box>
                </>
              )}
              {/* ---- FIM GPT ---- */}

              {/* STOP/RETURN não editam conteúdo extra */}
              {['STOP', 'RETURN', 'ROUTER'].includes(selectedNode?.data.type) ? null : (
                <>
                  {selectedNode?.data.type === 'CAPTURA' ? (
                    <>
                      <TextField
                        label="Campo para captura"
                        fullWidth
                        value={selectedNode?.data.capturaCampo || ''}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
                          if (val.length > 24) return;
                          const jaExiste = listarCamposCapturados(pages)
                            .filter((c) => c !== selectedNode?.data.capturaCampo)
                            .includes(val);
                          if (jaExiste) setErroCampo('Já existe outro campo com esse nome');
                          else setErroCampo('');
                          handleNodeDataChange('capturaCampo', val);
                        }}
                        sx={{ mb: 1 }}
                        error={!!erroCampo}
                        helperText={erroCampo ? erroCampo : 'Ex: NOME, CEP, TIME, NOME_CLIENTE'}
                        inputProps={{ maxLength: 24 }}
                      />
                      <Box sx={{ fontSize: 12, color: '#666', mb: 1, mt: -1 }}>
                        Apenas letras maiúsculas, números e _ (sem espaço)
                      </Box>
                    </>
                  ) : selectedNode?.data.type === 'VCARD' ? (
                    <>
                      <TextField
                        label="Nome do contato"
                        fullWidth
                        value={selectedNode?.data.nome || ''}
                        onChange={(e) => handleNodeDataChange('nome', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Digite o nome..."
                      />
                      <TextField
                        label="Telefone"
                        fullWidth
                        value={selectedNode?.data.telefone || ''}
                        onChange={(e) => handleNodeDataChange('telefone', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Digite o telefone..."
                      />
                    </>
                  ) : selectedNode?.data.type === 'FILE' ? (
                    <>
                      <TextField
                        label="Caminho do arquivo"
                        fullWidth
                        value={selectedNode?.data.caminho || ''}
                        onChange={(e) => handleNodeDataChange('caminho', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Ex: c:\\caminho\\file.ogg"
                      />
                      <TextField
                        label="Legenda (mensagem)"
                        fullWidth
                        multiline
                        rows={3}
                        value={selectedNode?.data.conteudo || ''}
                        onChange={(e) => handleNodeDataChange('conteudo', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Texto a ser enviada junto ao arquivo"
                      />

                      <Divider sx={{ my: 2 }} />

                      {renderCamposVariaveis(current.nodes, selectedNode?.data.conteudo, (v) => handleNodeDataChange('conteudo', v), 'FILE')}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedNode?.data.lembrete}
                            onChange={(e) => handleLembreteAtivoChange(e.target.checked)}
                          />
                        }
                        label="Enviar lembrete se o usuário não responder"
                        sx={{ mb: 1, ml: 0 }}
                      />
                      {!!selectedNode?.data.lembrete && (
                        <>
                          <TextField
                            label="Mensagem do lembrete"
                            multiline
                            rows={2}
                            fullWidth
                            value={selectedNode?.data.lembrete?.msg || ''}
                            onChange={(e) => handleLembreteChange('msg', e.target.value)}
                            sx={{ mb: 1 }}
                            placeholder="Digite a mensagem a ser enviada..."
                          />
                          <TextField
                            label="Quando enviar (minutos)"
                            type="number"
                            fullWidth
                            value={selectedNode?.data.lembrete?.tempo || ''}
                            onChange={(e) => handleLembreteChange('tempo', e.target.value.replace(/\D/g, ''))}
                            sx={{ mb: 2 }}
                            placeholder="Ex: 60 (para 1h), 5, 120..."
                            inputProps={{ min: 1, maxLength: 6 }}
                          />
                        </>
                      )}
                    </>
                  ) : selectedNode?.data.type === 'ELO' ? (
                    <>
                      <TextField
                        label="Destino (ID da etapa)"
                        fullWidth
                        value={selectedNode?.data.destino || ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          handleNodeDataChange('destino', val);
                        }}
                        sx={{ mb: 2 }}
                        helperText="Digite o ID da etapa de destino (apenas número)."
                        inputProps={{ maxLength: 8 }}
                        placeholder="Ex: 4"
                      />
                      <TextField
                        label="Destino de retorno (ID ao voltar)"
                        fullWidth
                        value={selectedNode?.data.retorno || ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          handleNodeDataChange('retorno', val);
                        }}
                        sx={{ mb: 2 }}
                        helperText="Para RETURN: pra onde volta quando terminar esse fluxo."
                        inputProps={{ maxLength: 8 }}
                        placeholder="Ex: 1"
                      />
                    </>
                  ) : selectedNode?.data.type === 'ATENDIMENTO' ? (
                    <>
                      <TextField
                        label="Mensagem para transferência"
                        multiline
                        rows={4}
                        fullWidth
                        value={selectedNode?.data.label || ''}
                        onChange={(e) => handleNodeDataChange('label', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Mensagem que será enviada ao transferir o atendimento"
                      />
                      {renderCamposVariaveis(current.nodes, selectedNode?.data.label, (v) => handleNodeDataChange('label', v), selectedNode?.data.type)}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedNode?.data.lembrete}
                            onChange={(e) => handleLembreteAtivoChange(e.target.checked)}
                          />
                        }
                        label="Enviar lembrete se o usuário não responder"
                        sx={{ mb: 1, ml: 0 }}
                      />
                      {!!selectedNode?.data.lembrete && (
                        <>
                          <TextField
                            label="Mensagem do lembrete"
                            multiline
                            rows={2}
                            fullWidth
                            value={selectedNode?.data.lembrete?.msg || ''}
                            onChange={(e) => handleLembreteChange('msg', e.target.value)}
                            sx={{ mb: 1 }}
                            placeholder="Digite a mensagem a ser enviada..."
                          />
                          <TextField
                            label="Quando enviar (minutos)"
                            type="number"
                            fullWidth
                            value={selectedNode?.data.lembrete?.tempo || ''}
                            onChange={(e) => handleLembreteChange('tempo', e.target.value.replace(/\D/g, ''))}
                            sx={{ mb: 2 }}
                            placeholder="Ex: 60 (para 1h), 5, 120..."
                            inputProps={{ min: 1, maxLength: 6 }}
                          />
                        </>
                      )}
                    </>
                  ) : isCadastrar || isExcluir ? (
                    <>
                      <TextField
                        label={isCadastrar ? 'Mensagem para cadastrar contato' : 'Mensagem para excluir contato'}
                        multiline
                        rows={4}
                        fullWidth
                        value={selectedNode?.data.label || ''}
                        onChange={(e) => handleNodeDataChange('label', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder={
                          isCadastrar
                            ? 'Opcional: mensagem que será enviada ao cadastrar contato'
                            : 'Opcional: mensagem que será enviada ao excluir contato'
                        }
                      />
                      {renderCamposVariaveis(current.nodes, selectedNode?.data.label, (v) => handleNodeDataChange('label', v), selectedNode?.data.type)}
                      <Typography variant="body2" sx={{ color: '#B22222', mt: -1, mb: 2 }}>
                        Se não preencher mensagem, só executa a ação e não envia texto.
                      </Typography>
                    </>
                  ) : (
                    <>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mb: 0.5,
                      }}
                    >
                      <Tooltip title="Expandir conteúdo">
                        <IconButton
                          size="small"
                          aria-label="Expandir conteúdo"
                          onClick={() =>
                            abrirEditorExpandido({
                              nodeId: selectedNode.id,
                              field: 'label',
                              title: `Conteúdo da etapa #${selectedNode.id}`,
                              value: selectedNode?.data.label || '',
                              nodeType: selectedNode?.data.type || 'TEXT',
                            })
                          }
                          sx={{
                            color: '#1976d2',
                            border: '1px solid #d0d7de',
                            borderRadius: 1,
                            width: 30,
                            height: 30,
                            '&:hover': {
                              bgcolor: '#eaf3ff',
                            },
                          }}
                        >
                          <OpenInFullIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  
                    <TextField
                      label="Conteúdo"
                      multiline
                      rows={5}
                      fullWidth
                      value={selectedNode?.data.label || ''}
                      onChange={(e) => handleNodeDataChange('label', e.target.value)}
                      sx={{ mb: 2 }}
                      placeholder={
                        selectedNode?.data.type === 'GPT'
                          ? GPT_PLACEHOLDER
                          : selectedNode?.data.type === 'CADASTRAR'
                          ? 'Mensagem para cadastrar contato'
                          : selectedNode?.data.type === 'EXCLUIR'
                          ? 'Mensagem para excluir contato'
                          : ''
                      }
                      InputProps={{
                        readOnly: selectedNode?.data.type === 'STOP',
                      }}
                    />
                  
                    {renderCamposVariaveis(
                      current.nodes,
                      selectedNode?.data.label,
                      (v) => handleNodeDataChange('label', v),
                      selectedNode?.data.type
                    )}
                  </>                  )}
                </>
              )}

              {/* Mostrar o rádio de INÍCIO para todos, menos STOP e RETURN */}
              {!['STOP', 'RETURN'].includes(selectedNode?.data.type) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={current.startNodeId === selectedNodeId}
                        onChange={handleStartChange}
                      />
                    }
                    label="Definir como início"
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              <Button
                variant="contained"
                color="error"
                onClick={deleteSelectedNode}
                fullWidth
                startIcon={<DeleteForeverIcon />}
                sx={{ fontWeight: 700, letterSpacing: 1, py: 1.3 }}
              >
                Apagar etapa
              </Button>
            </Box>
          );
        })()}
      </Box>
          <Dialog
            open={editorExpandido.open}
            onClose={fecharEditorExpandido}
            maxWidth={false}
            PaperProps={{
              sx: {
                width: {
                  xs: '94vw',
                  md: '56vw',
                },
                maxWidth: {
                  xs: '94vw',
                  md: '780px',
                },
                height: {
                  xs: '82vh',
                  md: '74vh',
                },
                maxHeight: {
                  xs: '82vh',
                  md: '74vh',
                },
              },
            }}
          >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6">
                  {editorExpandido.title}
                </Typography>
        
                <Typography variant="caption" color="text.secondary">
                  Tipo: {editorExpandido.nodeType}
                </Typography>
              </Box>
        
              <Typography variant="caption" color="text.secondary">
                {editorExpandido.value.length} caracteres ·{' '}
                {editorExpandido.value
                  ? editorExpandido.value.split('\n').length
                  : 0}{' '}
                linhas
              </Typography>
            </Box>
          </DialogTitle>
        
          <DialogContent
            dividers
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <TextField
              autoFocus
              multiline
              fullWidth
              value={editorExpandido.value}
              onChange={(e) =>
                setEditorExpandido((anterior) => ({
                  ...anterior,
                  value: e.target.value,
                }))
              }
              placeholder="Digite ou cole aqui todo o conteúdo da etapa"
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  height: '100%',
                  alignItems: 'flex-start',
                },
                '& textarea': {
                  height: '100% !important',
                  overflow: 'auto !important',
                  fontSize: 16,
                  lineHeight: 1.5,
                },
              }}
            />
            <Box sx={{ mt: 2 }}>
              {renderCamposVariaveis(
                current.nodes,
                editorExpandido.value,
                (novoValor) =>
                  setEditorExpandido((anterior) => ({
                    ...anterior,
                    value: novoValor,
                  })),
                editorExpandido.nodeType
              )}
            </Box>
          </DialogContent>
        
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              color="error"
              onClick={() =>
                setEditorExpandido((anterior) => ({
                  ...anterior,
                  value: '',
                }))
              }
            >
              Limpar
            </Button>
        
            <Box sx={{ flex: 1 }} />
        
            <Button onClick={fecharEditorExpandido}>
              Cancelar
            </Button>
        
            <Button
              variant="contained"
              onClick={salvarEditorExpandido}
            >
              Salvar conteúdo
            </Button>
          </DialogActions>
        </Dialog>
      {helpOpen && <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />}
    </Box>
  );
}